import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { Button, Card, Field, Input, Spinner, Stack } from '@dental-staffing-platform/ui';
import { resetPassword, validateResetToken } from '../auth-client.js';

export interface ResetPasswordProps {
  /** Token from the email link query string. */
  token: string | null;
  title?: string;
  loginHref?: string;
  forgotPasswordHref?: string;
}

/** Set a new password using a platform reset/invite token. */
export function ResetPassword({
  token,
  title = 'Choose a new password',
  loginHref = '/login',
  forgotPasswordHref = '/forgot-password',
}: ResetPasswordProps) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const guard = { active: true };
    void (async () => {
      if (!token) {
        if (guard.active) {
          setValid(false);
          setChecking(false);
        }
        return;
      }
      const ok = await validateResetToken(token);
      if (guard.active) {
        setValid(ok);
        setChecking(false);
      }
    })();
    return () => {
      guard.active = false;
    };
  }, [token]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!token) return;
    if (password !== confirm) {
      setError('Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      setMessage(await resetPassword(token, password));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not update password');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main
      style={{
        display: 'grid',
        placeItems: 'center',
        minHeight: '100vh',
        padding: '1.5rem',
        background: 'var(--dsp-bg)',
      }}
    >
      <Card style={{ width: '100%', maxWidth: 400, padding: '2rem' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <img src="/dpp-logo.png" alt="Dental Purchasing Partners" className="dsp-login-logo" />
          <h1 style={{ fontSize: '1.2rem', fontWeight: 800, marginTop: '1rem' }}>{title}</h1>
        </div>

        {checking ? (
          <Spinner label="Checking link…" />
        ) : message ? (
          <Stack gap={14}>
            <p style={{ fontSize: '0.95rem' }}>{message}</p>
            <a
              href={loginHref}
              className="dsp-btn dsp-btn--primary dsp-btn--md"
              style={{ textAlign: 'center' }}
            >
              Sign in
            </a>
          </Stack>
        ) : !token || !valid ? (
          <Stack gap={14}>
            <p className="dsp-field__error">This reset link is invalid or has expired.</p>
            <a
              href={forgotPasswordHref}
              className="dsp-muted"
              style={{ fontSize: '0.88rem', textAlign: 'center' }}
            >
              Request a new link
            </a>
          </Stack>
        ) : (
          <form onSubmit={(e) => void onSubmit(e)}>
            <Stack gap={14}>
              <Field label="New password">
                <Input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </Field>
              <Field label="Confirm password">
                <Input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={8}
                />
              </Field>
              <p className="dsp-muted" style={{ fontSize: '0.78rem' }}>
                Use 8+ characters with upper, lower, number, and a special character.
              </p>
              <Button type="submit" disabled={busy}>
                {busy ? 'Saving…' : 'Update password'}
              </Button>
              {error ? <span className="dsp-field__error">{error}</span> : null}
            </Stack>
          </form>
        )}
      </Card>
    </main>
  );
}
